import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { queryKeys } from '../config/queryClient';
import { configAPI } from '../services/configAPI';

// Список игр и режимов меняется редко, поэтому последний ответ хранится в браузере: меню и экран игры
// появляются сразу, а свежие данные подтягиваются фоном (если запись старше staleTime).
// Срок свежести — минута, как у ответа сервера: при 30 минутах после смены режима (время, число карт) список
// на телефоне ещё полчаса показывал старое, а описание режима — уже новое
const CONFIG_STALE_TIME = 60 * 1000;
const STORE_PREFIX = 'cfg:v1:';
const STORE_MAX_AGE = 7 * 24 * 60 * 60 * 1000;

interface Stored<T> {
  data: T;
  at: number;
}

function readStored<T>(key: string): Stored<T> | undefined {
  try {
    const raw = window.localStorage.getItem(STORE_PREFIX + key);
    if (!raw) return undefined;
    const stored = JSON.parse(raw) as Stored<T>;
    return Date.now() - stored.at < STORE_MAX_AGE ? stored : undefined;
  } catch {
    return undefined;
  }
}

async function fetchAndStore<T>(key: string, load: () => Promise<T>): Promise<T> {
  const data = await load();
  try {
    window.localStorage.setItem(STORE_PREFIX + key, JSON.stringify({ data, at: Date.now() } satisfies Stored<T>));
  } catch {
    // хранилище недоступно или переполнено: просто работаем без кэша
  }
  return data;
}

// Hook для получения списка игр
export function useGames() {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const stored = readStored<Awaited<ReturnType<typeof configAPI.getGames>>>(`games:${currentLang}`);

  return useQuery({
    queryKey: [...queryKeys.games, currentLang], // Включаем язык в ключ
    queryFn: () => fetchAndStore(`games:${currentLang}`, () => configAPI.getGames()),
    initialData: stored?.data,
    initialDataUpdatedAt: stored?.at,
    staleTime: CONFIG_STALE_TIME,
  });
}

// Hook для получения режимов игры
export function useGameModes(gameId: string) {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const stored = gameId ? readStored<Awaited<ReturnType<typeof configAPI.getGameModes>>>(`modes:${gameId}:${currentLang}`) : undefined;

  return useQuery({
    queryKey: [...queryKeys.gameModes(gameId), currentLang], // Включаем язык в ключ
    queryFn: () => fetchAndStore(`modes:${gameId}:${currentLang}`, () => configAPI.getGameModes(gameId)),
    initialData: stored?.data,
    initialDataUpdatedAt: stored?.at,
    staleTime: CONFIG_STALE_TIME,
    enabled: !!gameId, // Выполнять только если gameId существует
  });
}

// Hook для получения конкретного режима
export function useGameMode(gameId: string, modeId: string) {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  
  return useQuery({
    queryKey: [...queryKeys.gameMode(gameId, modeId), currentLang], // Включаем язык в ключ
    queryFn: () => configAPI.getGameMode(gameId, modeId),
    staleTime: CONFIG_STALE_TIME,
    enabled: !!gameId && !!modeId,
  });
}
